import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxInvoicesIncoming,
  type TaxInvoiceIncomingListItem,
} from '@/api/taxInvoices';
import { goodsReceipts } from '@/api/goodsReceipts';
import DatePopover from '@/components/DatePopover';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);

export default function TaxInvoicesIncomingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [selOrg, setSelOrg] = useState('');
  const [selReceipt, setSelReceipt] = useState('');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(today());
  const [registered, setRegistered] = useState(true);
  const [registrationDate, setRegistrationDate] = useState(today());

  const list = useQuery({
    queryKey: ['tax-invoices-incoming'],
    queryFn: () => taxInvoicesIncoming.list({ limit: 200 }),
  });
  // UX β: org dropdown через usePermissionsByOrg, окремий orgs.list не потрібен
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-posted'],
    queryFn: () => goodsReceipts.list({ posted: true, limit: 200 }),
    enabled: createOpen,
  });

  const create = useMutation({
    mutationFn: () => taxInvoicesIncoming.createFromReceipt({
      organization_id: selOrg,
      base_document_type: 'doc.goods_receipts',
      base_document_id: selReceipt,
      invoice_number: number,
      invoice_date: date,
      is_registered_erpn: registered,
      registration_date: registered ? registrationDate : undefined,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      setCreateOpen(false);
      navigate(`/tax-invoices-incoming/${doc.id}`);
    },
  });

  const cols = useMemo<ColumnDef<TaxInvoiceIncomingListItem>[]>(() => [
    { key: 'invoice_number', header: '№ НН', width: 150, importBadge: true, sortValue: (r) => r.invoice_number ?? '', cell: (r) => r.invoice_number },
    {
      key: 'invoice_date', header: t('cd.date'), width: 130,
      sortValue: (r) => r.invoice_date ?? '',
      cell: (r) => r.invoice_date ? new Date(r.invoice_date).toLocaleDateString(i18n.language) : '',
    },
    { key: 'contractor_name', header: 'Постачальник', sortValue: (r) => r.contractor_name ?? '', cell: (r) => r.contractor_name },
    {
      key: 'total_amount', header: t('cd.amount'), width: 130, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt(r.total_amount),
    },
    {
      key: 'is_registered_erpn', header: 'Зареєстр. ЄРПН', width: 140, align: 'center',
      sortValue: (r) => r.is_registered_erpn ? 1 : 0,
      cell: (r) => (
        <span style={{ fontWeight: 600, color: r.is_registered_erpn ? '#15803d' : '#b91c1c' }}>
          {r.is_registered_erpn ? '✓' : '✗'}
        </span>
      ),
    },
    {
      key: 'posted', header: t('cd.posted'), width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no'),
    },
  ], [t, i18n.language]);

  // UX β: фільтр org dropdown по tax.invoice.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('tax.invoice.manage');
  if (!selOrg && availableOrgs[0]) setSelOrg(availableOrgs[0].id);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Податкові накладні (вхідні)</h2>
        <button
          className="primary"
          onClick={() => setCreateOpen(true)}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати податкові накладні у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Створити з поступлення
        </button>
      </div>
      <DataTable<TaxInvoiceIncomingListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/tax-invoices-incoming/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="invoice_date"
        initialSortDir="desc"
      />

      <Drawer
        open={createOpen}
        title="Нова податкова накладна (вхідна)"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!selReceipt || !number || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.create')}
            </button>
          </>
        }
      >
        <div className="field">
          <label>{t('gs.organization')}</label>
          <select value={selOrg} onChange={(e) => setSelOrg(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>Документ-підстава (надходження)</label>
          <select value={selReceipt} onChange={(e) => setSelReceipt(e.target.value)}>
            <option value="">—</option>
            {receiptsQ.data?.items.map((r) => (
              <option key={r.id} value={r.id}>
                {r.document_number} ({new Date(r.document_date).toLocaleDateString('uk-UA')}) — {fmt(r.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>№ НН постачальника</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </div>
          <div className="field">
            <label>Дата НН</label>
            <DatePopover value={date} onChange={(v) => setDate(v)} />
          </div>
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={registered}
              onChange={(e) => setRegistered(e.target.checked)}
              style={{ width: 'auto', marginRight: '0.5rem' }}
            />
            Зареєстрована в ЄРПН (без неї ПК не йде на 6441)
          </label>
        </div>
        {registered && (
          <div className="field">
            <label>Дата реєстрації</label>
            <DatePopover value={registrationDate} onChange={(v) => setRegistrationDate(v)} />
          </div>
        )}
      </Drawer>
    </>
  );
}
